import React from 'react';
import { ColumnSettings, ExcelService } from 'src/utils/excel.service';

export default function useExcel<T extends object>(settings: ColumnSettings<T>) {
  const service = React.useMemo(() => {
    return new ExcelService(settings);
  }, [settings]);

  const [template, setTemplate] = React.useState<
    | { service: ExcelService<T>; url: string; size: number; type: string }
    | { service: ExcelService<T>; error: unknown }
  >();

  React.useEffect(() => {
    let active = true;
    let objectUrl: string | undefined;

    void service.ready
      .then((blob) => {
        if (!active) return;

        objectUrl = URL.createObjectURL(blob);
        setTemplate({
          service,
          url: objectUrl,
          size: blob.size,
          type: blob.type,
        });
      })
      .catch((error: unknown) => {
        if (active) {
          setTemplate({ service, error });
        }
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [service]);

  const currentTemplate = template?.service === service ? template : undefined;
  const isReady = currentTemplate !== undefined && 'url' in currentTemplate;
  const hasError = currentTemplate !== undefined && 'error' in currentTemplate;
  const url = isReady ? currentTemplate.url : undefined;
  const size = isReady ? currentTemplate.size : undefined;
  const type = isReady ? currentTemplate.type : undefined;
  const error = hasError ? currentTemplate.error : undefined;

  const downloadFile = (fileName?: string) => {
    return service.downloadBlob(fileName);
  };

  const importFile = async (file: File): Promise<Array<T>> => {
    return await service.importFile(file);
  };

  return {
    url,
    size,
    type,
    isReady,
    hasError,
    error,
    downloadFile,
    importFile,
  };
}
